import type { ReactNode } from 'react'

import {
  chromeToolbarButtonClass,
  chromeToolbarDangerClass,
} from './ChromeSurface'
import { ThemeModal } from './modal'

/** 列表行操作按钮：与顶栏次要按钮、Select 表面一致 */
export function managementListActionClass(
  angular: boolean,
  variant: 'default' | 'danger' = 'default',
) {
  const shell =
    variant === 'danger'
      ? chromeToolbarDangerClass(angular)
      : chromeToolbarButtonClass(angular)
  return [shell, 'sm:px-3 sm:py-2 sm:text-sm'].join(' ')
}

/** 弹窗内紧凑行操作（更小命中区、单行对齐） */
export function managementCompactActionClass(
  angular: boolean,
  variant: 'default' | 'danger' = 'default',
) {
  const shell =
    variant === 'danger'
      ? chromeToolbarDangerClass(angular)
      : chromeToolbarButtonClass(angular)
  return [shell, 'px-2 py-1 text-[11px] font-semibold sm:px-2.5 sm:py-1.5 sm:text-xs'].join(' ')
}

export type ManagementListEntry = {
  id: string
  /** 主标题 */
  primary: string
  /** 角标，如风格标签 */
  badge?: string
  /** 次要说明（可多行，自动截断） */
  detail?: string
  /** 状态指示器（如小圆点），放在标题旁边 */
  statusIndicator?: ReactNode
  actions: ReactNode
}

type BodyProps = {
  entries: ManagementListEntry[]
  angular: boolean
  emptyText?: string
  listTitle?: string
}

/**
 * 可嵌入任意 ThemeModal，或与 {@link ManagementListModal} 组合使用。
 */
export function ManagementListBody({
  entries,
  angular,
  emptyText = '暂无条目',
  listTitle,
}: BodyProps) {
  const cardRound = angular ? 'rounded-none' : 'rounded-[var(--radius-sm)]'
  const badgeRound = angular ? 'rounded-none' : 'rounded-[var(--radius-sm)]'

  return (
    <div>
      {listTitle ? (
        <p className="typo-lib-chip mb-2 font-semibold text-ink">{listTitle}</p>
      ) : null}
      {entries.length === 0 ? (
        <p className="typo-lib-empty py-6 text-center">{emptyText}</p>
      ) : (
        <ul className="space-y-2">
          {entries.map((e) => (
            <li
              key={e.id}
              className={[
                'flex flex-col gap-2 border border-line bg-panel p-3 sm:flex-row sm:items-center sm:justify-between',
                cardRound,
              ].join(' ')}
            >
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  {e.statusIndicator ? (
                    <span className="shrink-0">{e.statusIndicator}</span>
                  ) : null}
                  <span className="typo-lib-card-title truncate">{e.primary}</span>
                  {e.badge ? (
                    <span
                      className={[
                        'typo-lib-hint shrink-0 border border-line bg-surface-muted px-1.5 py-0 text-[10px] font-medium',
                        badgeRound,
                      ].join(' ')}
                    >
                      {e.badge}
                    </span>
                  ) : null}
                </div>
                {e.detail ? (
                  <p className="typo-lib-card-meta mt-1 line-clamp-2 leading-relaxed">
                    {e.detail}
                  </p>
                ) : null}
              </div>
              <div className="flex shrink-0 flex-wrap gap-2 sm:justify-end">{e.actions}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

type ModalProps = {
  open: boolean
  onClose: () => void
  title: string
  angular?: boolean
  size?: 'sm' | 'md' | 'lg'
  entries: ManagementListEntry[]
  footer?: ReactNode
  emptyText?: string
  listTitle?: string
}

/** 仅含列表 + 可选底部的管理弹窗（分类、文件夹等） */
export function ManagementListModal({
  open,
  onClose,
  title,
  angular = false,
  size = 'md',
  entries,
  footer,
  emptyText,
  listTitle,
}: ModalProps) {
  return (
    <ThemeModal
      open={open}
      onClose={onClose}
      title={title}
      angular={angular}
      size={size}
      footer={footer}
    >
      <ManagementListBody
        entries={entries}
        angular={angular}
        emptyText={emptyText}
        listTitle={listTitle}
      />
    </ThemeModal>
  )
}
